import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { ComingSoonBadge } from '@/components/common/ComingSoonBadge';
import type { StartupProfileData } from '@/features/profiles/api/startup-profile.api';
import { useEntityProfile } from '@/features/profiles/hooks/useEntityProfile';
import { useIssueReportOffer } from '@/hooks/useIssueReportOffer';
import { usePermission } from '@/hooks/usePermission';
import { ISSUE_REPORT_OFFER_STATE } from '@/lib/constants/issue-report';
import { PERMISSIONS } from '@/lib/constants/permissions';
import type { ActiveMembership } from '@/lib/types/auth.types';
import { DATA_ROOM_STRINGS, DATA_ROOM_VOICE, type DataRoomVoice } from '../constants';
import type { DataRoomEntitlement, RefusalReason } from '../api/data-room.api';
import { useDataRoomEntitlement } from '../hooks/useDataRoomEntitlement';

/** The Issue Report card stands out like the navbar's Issue Report CTA; the Command Center is a plain primary action. */
const ACTION_VARIANT: Record<DataRoomVoice, 'yellow' | 'primary'> = {
  command_center: 'primary',
  issue_report: 'yellow',
};

/**
 * Refusals that only happen while the startup HAS a live service: the service
 * exists, this user just holds no seat in it.
 */
const SEAT_REFUSALS: ReadonlySet<RefusalReason> = new Set(['seat_not_assigned', 'seat_limit_reached']);

/**
 * Which card to show. The Command Center only while Premium access is live:
 * an entitled Premium/subscription service, or a seat refusal on an SR NEXT
 * startup (Premium is live, this member just has no seat). Anything else
 * (no access, ended or cancelled Premium, an Issue Report) is the Issue Report
 * card. The SR NEXT flag alone no longer decides it: it outlives a cancelled
 * Premium, which left ex-Premium startups looking at the Command Center
 * (2026-10-14, reverses the 2026-10-12 flag-only rule).
 */
function resolveVoice(entitlement: DataRoomEntitlement | undefined, isSrNextClient: boolean): DataRoomVoice {
  if (!entitlement) return 'issue_report';
  if (entitlement.entitled) return DATA_ROOM_VOICE[entitlement.service];
  return isSrNextClient && SEAT_REFUSALS.has(entitlement.reason) ? 'command_center' : 'issue_report';
}

export interface DataRoomDashboardCardProps {
  /** The acting startup membership. */
  membership: ActiveMembership;
}

/**
 * The dashboard's top-right data-room card (client copy, 2026-10-12), shown
 * to every Startup Admin and Member:
 *
 * - Premium access live: "Access Your Legal Command Center".
 * - Anything else: "What Could Stall Your Raise?".
 *
 * Both lead to the Data Room page, which owns every access decision and its
 * explanation; the card only picks its wording (see `resolveVoice`). The one
 * exception is a buyer with no access yet, sent straight to purchase (MYN-284).
 */
export function DataRoomDashboardCard({ membership }: DataRoomDashboardCardProps) {
  const { data } = useEntityProfile(membership);
  const entitlement = useDataRoomEntitlement(membership.entityId);
  const canBuy = usePermission(PERMISSIONS.BILLING_MANAGE);
  // Only a buyer is ever offered the purchase, so only a buyer needs to know
  // whether it is on sale (FR-203).
  const offer = useIssueReportOffer({ enabled: canBuy });
  const profile = data as StartupProfileData | undefined;
  // Wait for both answers, so the card never flashes the wrong voice.
  if (!profile || entitlement.isLoading) return null;

  const kind = resolveVoice(entitlement.data, Boolean(profile.isSrNextClient));
  const copy = DATA_ROOM_STRINGS.DASHBOARD_CARD[kind];
  // Only someone who can buy, with nothing bought yet, goes to the purchase
  // page; a member or a startup that already has a report keeps the Data Room
  // page, which explains or opens what they have (MYN-284).
  const isPurchase = kind === 'issue_report' && canBuy && !entitlement.data?.entitled;
  const action = isPurchase ? DATA_ROOM_STRINGS.DASHBOARD_CARD_PURCHASE : copy;
  // The buy action follows the offer (FR-203): "Coming Soon" while no plan is on
  // sale, and nothing while the offer is loading or failed, so a buy button never
  // flashes and turns into "Coming Soon". The card's text stays in every case.
  const offerState = offer.data?.state;
  let cta: ReactNode = (
    <Button asChild variant={ACTION_VARIANT[kind]} className="mt-4 inline-flex">
      <Link to={action.to}>{action.action}</Link>
    </Button>
  );
  if (isPurchase && offerState !== ISSUE_REPORT_OFFER_STATE.AVAILABLE) {
    cta = offerState === ISSUE_REPORT_OFFER_STATE.COMING_SOON ? <ComingSoonBadge className="mt-4" /> : null;
  }

  return (
    <div className="rounded-card border-navy/10 border p-6 shadow-[var(--card-shadow)]">
      <h3 className="text-h4 text-navy">{copy.title}</h3>
      <p className="text-navy/70 mt-1 text-sm">{copy.body}</p>
      {cta}
    </div>
  );
}
